import { onAnimationEnd } from './motion-utils.js';

const DRAWER_ROOT_SELECTOR = '[data-drawer-id]';
const OPEN_DRAWER_SELECTOR = '[data-drawer-id].is-open';
const DRAWER_OPEN_SELECTOR = '[data-drawer-open]';
const DRAWER_CLOSE_SELECTOR = '[data-drawer-close]';
const DRAWER_CONTENT_SELECTOR = '[data-drawer-content]';
const DRAWER_BACKDROP_SELECTOR = '[data-drawer-backdrop]';
const DRAWER_OPEN_CLASS = 'is-open';
const DRAWER_CONTENT_ENTER_CLASS = 'drawer-content-enter-active';
const DRAWER_CONTENT_LEAVE_CLASS = 'drawer-content-leave-active';
const DRAWER_BACKDROP_ENTER_CLASS = 'drawer-backdrop-enter-active';
const DRAWER_BACKDROP_LEAVE_CLASS = 'drawer-backdrop-leave-active';

const openStack = [];
const lastTriggerById = new Map();

// Reads a drawer identifier from supported attributes.
const getDrawerId = (element) => element?.dataset?.drawerId || null;

// Escapes a dynamic value before building a CSS selector.
const escapeCss = (value) => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }

  return `${value}`.replace(/"/g, '\\"');
};

// Finds a drawer element by its shared ID contract.
const getDrawerById = (id) => document.querySelector(`[data-drawer-id="${escapeCss(id)}"]`);

// Returns whether the drawer is currently visible.
const isDrawerOpen = (drawer) => drawer.classList.contains(DRAWER_OPEN_CLASS);

// Removes a drawer ID from the open stack.
const removeFromStack = (id) => {
  const index = openStack.lastIndexOf(id);
  if (index >= 0) {
    openStack.splice(index, 1);
  }
};

// Syncs aria-expanded across all triggers for a drawer.
const syncTriggerState = (id, expanded) => {
  const triggers = document.querySelectorAll(`${DRAWER_OPEN_SELECTOR}[data-drawer-open="${escapeCss(id)}"]`);
  for (const trigger of triggers) {
    if (!(trigger instanceof HTMLElement)) continue;
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }
};

// Locks page scrolling while any drawer is open.
const syncScrollLock = () => {
  const hasOpenDrawer = Boolean(document.querySelector(OPEN_DRAWER_SELECTOR));
  document.body.style.overflow = hasOpenDrawer ? 'hidden' : '';
};

// Focuses the preferred initial element when a drawer opens.
const focusInitialElement = (drawer) => {
  const content = drawer.querySelector(DRAWER_CONTENT_SELECTOR);
  if (!(content instanceof HTMLElement)) return;

  const closeTarget = content.querySelector(DRAWER_CLOSE_SELECTOR);
  const fallbackTarget = content.querySelector('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])');
  const focusTarget = closeTarget instanceof HTMLElement ? closeTarget : fallbackTarget;
  if (!(focusTarget instanceof HTMLElement)) return;

  requestAnimationFrame(() => focusTarget.focus());
};

// Returns the animated content element for a drawer.
const getMotionElement = (drawer) => {
  const content = drawer.querySelector(DRAWER_CONTENT_SELECTOR);
  return content instanceof HTMLElement ? content : null;
};

// Returns the backdrop element for enter/leave animations.
const getBackdropElement = (drawer) => {
  const backdrop = drawer.querySelector(DRAWER_BACKDROP_SELECTOR);
  return backdrop instanceof HTMLElement ? backdrop : null;
};

// Removes transient animation classes from the motion element.
const clearMotionClasses = (element) => {
  if (!(element instanceof HTMLElement)) return;
  element.classList.remove(DRAWER_CONTENT_ENTER_CLASS, DRAWER_CONTENT_LEAVE_CLASS);
};

// Removes transient backdrop animation classes.
const clearBackdropClasses = (element) => {
  if (!(element instanceof HTMLElement)) return;
  element.classList.remove(DRAWER_BACKDROP_ENTER_CLASS, DRAWER_BACKDROP_LEAVE_CLASS);
};

// Emits lifecycle events used by feature-specific scripts.
const emitDrawerEvent = (name, drawer, extra = {}) => {
  const id = getDrawerId(drawer) || '';
  document.dispatchEvent(
    new CustomEvent(name, {
      detail: { id, drawer, ...extra },
    })
  );
};

// Opens a drawer, updates a11y state, and records trigger context.
// `options.initialTab` is forwarded for cart drawer (wishlist vs cart); see cart-drawer-tabs.js.
export const openDrawer = async (id, trigger = null, options = {}) => {
  const drawer = getDrawerById(id);
  if (!(drawer instanceof HTMLElement)) return;
  if (isDrawerOpen(drawer)) return;

  if (trigger instanceof HTMLElement) {
    lastTriggerById.set(id, trigger);
  }

  drawer.classList.add(DRAWER_OPEN_CLASS);
  const motionElement = getMotionElement(drawer);
  const backdropElement = getBackdropElement(drawer);

  clearMotionClasses(motionElement);
  clearBackdropClasses(backdropElement);

  if (motionElement instanceof HTMLElement) {
    motionElement.classList.add(DRAWER_CONTENT_ENTER_CLASS);
    void onAnimationEnd(motionElement, () => {
      motionElement.classList.remove(DRAWER_CONTENT_ENTER_CLASS);
    });
  }
  if (backdropElement instanceof HTMLElement) {
    backdropElement.classList.add(DRAWER_BACKDROP_ENTER_CLASS);
    void onAnimationEnd(backdropElement, () => {
      backdropElement.classList.remove(DRAWER_BACKDROP_ENTER_CLASS);
    });
  }

  removeFromStack(id);
  openStack.push(id);
  syncTriggerState(id, true);
  syncScrollLock();
  focusInitialElement(drawer);
  emitDrawerEvent('drawer:opened', drawer, { trigger, initialTab: options.initialTab });
};

// Closes a drawer and restores focus to its opening trigger.
export const closeDrawer = async (id) => {
  const drawer = getDrawerById(id);
  if (!(drawer instanceof HTMLElement)) return;
  if (!isDrawerOpen(drawer)) return;

  const motionElement = getMotionElement(drawer);
  const backdropElement = getBackdropElement(drawer);

  if (motionElement instanceof HTMLElement) {
    if (motionElement.classList.contains(DRAWER_CONTENT_LEAVE_CLASS)) return;
    motionElement.classList.remove(DRAWER_CONTENT_ENTER_CLASS);
    motionElement.classList.add(DRAWER_CONTENT_LEAVE_CLASS);
  }
  if (backdropElement instanceof HTMLElement) {
    backdropElement.classList.remove(DRAWER_BACKDROP_ENTER_CLASS);
    backdropElement.classList.add(DRAWER_BACKDROP_LEAVE_CLASS);
  }

  const leaveAnimations = [];
  if (motionElement instanceof HTMLElement) {
    leaveAnimations.push(
      onAnimationEnd(motionElement, () => {
        motionElement.classList.remove(DRAWER_CONTENT_LEAVE_CLASS);
      })
    );
  }
  if (backdropElement instanceof HTMLElement) {
    leaveAnimations.push(
      onAnimationEnd(backdropElement, () => {
        backdropElement.classList.remove(DRAWER_BACKDROP_LEAVE_CLASS);
      })
    );
  }
  if (leaveAnimations.length) {
    await Promise.all(leaveAnimations);
  }

  drawer.classList.remove(DRAWER_OPEN_CLASS);
  removeFromStack(id);
  syncTriggerState(id, false);
  syncScrollLock();
  emitDrawerEvent('drawer:closed', drawer);

  const trigger = lastTriggerById.get(id);
  if (trigger instanceof HTMLElement && document.contains(trigger)) {
    trigger.focus();
  }

  lastTriggerById.delete(id);
};

// Recalculates trigger state and scroll lock for a drawer.
export const syncDrawerState = (id) => {
  const drawer = getDrawerById(id);
  if (!(drawer instanceof HTMLElement)) return;
  syncTriggerState(id, isDrawerOpen(drawer));
  syncScrollLock();
};

// Handles delegated click open/close actions.
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const openTrigger = target.closest(DRAWER_OPEN_SELECTOR);
  if (openTrigger instanceof HTMLElement) {
    const id = openTrigger.dataset.drawerOpen;
    if (!id) return;
    const drawer = getDrawerById(id);
    if (!(drawer instanceof HTMLElement)) return;

    event.preventDefault();
    if (isDrawerOpen(drawer)) {
      void closeDrawer(id);
      return;
    }

    void openDrawer(id, openTrigger);
    return;
  }

  const closeTrigger = target.closest(DRAWER_CLOSE_SELECTOR);
  if (closeTrigger instanceof HTMLElement) {
    const id = closeTrigger.dataset.drawerClose;
    if (!id) return;
    event.preventDefault();
    void closeDrawer(id);
    return;
  }

  const backdrop = target.closest(DRAWER_BACKDROP_SELECTOR);
  if (backdrop instanceof HTMLElement) {
    const drawer = backdrop.closest(DRAWER_ROOT_SELECTOR);
    if (!(drawer instanceof HTMLElement)) return;

    const id = getDrawerId(drawer);
    if (!id) return;

    event.preventDefault();
    void closeDrawer(id);
    return;
  }
});

// Closes the last opened drawer on Escape.
document.addEventListener('keyup', (event) => {
  if (event.key !== 'Escape') return;

  const id = openStack[openStack.length - 1];
  if (!id) return;
  void closeDrawer(id);
});

// Opens a drawer when another module requests it.
document.addEventListener('drawer:open-request', (event) => {
  const detail = event.detail || {};
  const id = typeof detail.id === 'string' ? detail.id : '';
  if (!id) return;
  const trigger = detail.trigger instanceof HTMLElement ? detail.trigger : null;
  void openDrawer(id, trigger, { initialTab: detail.initialTab });
});

// Re-syncs drawer state when another module requests it.
document.addEventListener('drawer:sync-request', (event) => {
  const detail = event.detail || {};
  const id = typeof detail.id === 'string' ? detail.id : '';
  if (!id) return;
  syncDrawerState(id);
});

// Initializes trigger and scroll state from existing markup.
const initDrawers = () => {
  const drawers = document.querySelectorAll(DRAWER_ROOT_SELECTOR);
  for (const drawer of drawers) {
    if (!(drawer instanceof HTMLElement)) continue;
    const id = getDrawerId(drawer);
    if (!id) continue;
    syncTriggerState(id, isDrawerOpen(drawer));
  }

  syncScrollLock();
};

initDrawers();
